import { useState } from 'react';
import { Card } from '../components/common';
import { API_BASE_URL } from '../services/api';

export default function DocsPage() {
  const baseUrl = API_BASE_URL || window.location.origin;
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'guide' | 'api' | 'faq'>('guide');

  const ParamRow = ({ name, type, required, desc }: { name: string; type: string; required: boolean; desc: string }) => (
    <tr><td className="px-4 py-2"><code>{name}</code></td><td className="px-4 py-2">{type}</td><td className="px-4 py-2">{required ? '是' : '否'}</td><td className="px-4 py-2">{desc}</td></tr>
  );

  const generateMarkdownDoc = () => {
    return `# 大豐環保爬蟲工具 使用指南

## 快速開始

### 第一步：取得 API Key
1. 登入系統後，點擊左側選單的「API Key 管理」
2. 點擊「建立 API Key」按鈕
3. 輸入描述（例如：測試用、生產環境）
4. 複製生成的 API Key（格式：jpp_xxxxxxxx...）

提示：您可以隨時在 API Key 列表中點擊複製按鈕來複製 Key。

### 第二步：測試 API
1. 點擊左側選單的「測試區」
2. 選擇剛才建立的 API Key
3. 輸入要爬取的網址
4. 點擊「發送請求」
5. 查看回應結果

### 第三步：整合到您的專案
使用以下程式碼範例，將 API 整合到您的專案中。

## 前端功能說明

### 儀表板
- 查看請求統計（今日、本週、本月）
- 查看成功率、Token 使用量、流量統計
- 查看最近的請求紀錄
- 可按日期範圍篩選統計數據

### API Key 管理
- 建立新的 API Key
- 啟用/停用 API Key
- 刪除不需要的 API Key
- 每個 API Key 都有獨立的描述，方便管理

### 測試區（Playground）
- 選擇 API Key 進行測試
- 輸入目標網址
- 調整各種參數（格式、引擎、選擇器等）
- 即時查看 API 回應結果
- 適合在正式整合前測試參數效果

### 請求日誌
- 查看所有 API 請求的歷史紀錄
- 包含：時間、網址、狀態、耗時、Token 使用量
- 可按日期、狀態篩選
- 點擊紀錄可查看詳細資訊

### 個人資料
- 查看個人統計（總請求數、成功率、Token 使用量）
- 查看預估成本（每百萬 Token $0.05）
- 修改密碼

## API 技術文檔

### 基本資訊
- API 端點: ${baseUrl}/api/v1/read
- 認證方式: Bearer Token (API Key)

### 認證方式
所有 API 請求都需要在 Header 中包含 API Key：
\`\`\`
Authorization: Bearer YOUR_API_KEY
\`\`\`

### 速率限制
本服務為多用戶共享平台，目前不設硬性 RPM 限制，但實際可用速率約為 20 RPM 左右。
- 建議請求間隔至少 3 秒
- 批量處理時使用佇列機制
- 遇到 502 錯誤時，等待 10-30 秒後重試

### 回應時間與超時設置

#### 回應時間參考
| 頁面類型 | 預估時間 |
|---------|---------|
| 簡單靜態頁面 | 5-15 秒 |
| 一般動態頁面 | 15-30 秒 |
| 複雜/長內容頁面（如法律條文、長文章） | 30-60+ 秒 |

#### 客戶端超時建議
- 建議設置至少 **120 秒**（2 分鐘）
- 使用 \`browser\` 引擎或爬取長頁面，建議 **180 秒**（3 分鐘）
- 請勿使用過短的超時時間（如 30 秒），否則長頁面可能會超時失敗

### POST /api/v1/read - 爬取網頁內容

#### 基本參數
| 參數 | 類型 | 必填 | 說明 |
|------|------|------|------|
| url | string | 是 | 目標網址 |
| engine | string | 否 | 讀取引擎：browser（品質優先）、direct（速度優先） |
| format | string | 否 | 內容格式：markdown（預設）、html、text、screenshot、pageshot |
| json_response | boolean | 否 | 固定為 true，不可關閉 |
| timeout | number | 否 | 超時時間（1-180秒），預設 30 秒 |
| token_budget | number | 否 | 詞元預算限制 |

#### CSS 選擇器
| 參數 | 類型 | 必填 | 說明 |
|------|------|------|------|
| target_selector | string | 否 | 僅提取匹配的元素 |
| wait_for_selector | string | 否 | 等待指定元素出現後再提取 |
| exclude_selector | string | 否 | 排除指定元素 |

#### 快取與代理
| 參數 | 類型 | 必填 | 說明 |
|------|------|------|------|
| bypass_cache | boolean | 否 | 繞過快取 |
| no_cache_no_track | boolean | 否 | 請勿快取或追蹤 |
| use_proxy | boolean | 否 | 使用代理服務器 |
| proxy_country | string | 否 | 代理國家代碼（TW、US、JP） |

## 程式碼範例

### cURL 基本請求
\`\`\`bash
curl -X POST "${baseUrl}/api/v1/read" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"url": "https://example.com"}'
\`\`\`

### JavaScript (fetch)
\`\`\`javascript
const response = await fetch('${baseUrl}/api/v1/read', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    url: 'https://example.com',
    format: 'markdown',
    json_response: true
  })
});
const data = await response.json();
\`\`\`

### Python (requests)
\`\`\`python
import requests

response = requests.post(
    '${baseUrl}/api/v1/read',
    headers={
        'Authorization': 'Bearer YOUR_API_KEY',
        'Content-Type': 'application/json'
    },
    json={
        'url': 'https://example.com',
        'format': 'markdown',
        'json_response': True
    },
    timeout=120  # 建議至少 120 秒
)
data = response.json()
\`\`\`

## 回應格式

### 成功回應
\`\`\`json
{
  "code": 200,
  "status": 20000,
  "data": {
    "title": "Example Domain",
    "url": "https://example.com/",
    "content": "# Example Domain...",
    "usage": { "tokens": 150 }
  }
}
\`\`\`

### 錯誤回應
\`\`\`json
{
  "success": false,
  "error": {
    "code": "VALIDATION_ERROR",
    "message": "缺少必要參數: url"
  }
}
\`\`\`

## 錯誤代碼
| HTTP 狀態碼 | 錯誤代碼 | 說明 | 處理方式 |
|------------|---------|------|---------|
| 400 | VALIDATION_ERROR | 請求參數錯誤 | 檢查參數格式，確保 url 參數存在 |
| 401 | INVALID_TOKEN | API Key 無效 | 檢查 API Key 是否正確、是否已啟用 |
| 502 | ALL_RETRIES_FAILED | 系統負載過高 | 等待 10-30 秒後重試 |

## 常見問題 FAQ

### Q: 為什麼請求一直超時？
A: 可能原因：
1. 目標網站回應慢或內容很長
2. 客戶端超時設置太短（建議至少 120 秒）
3. 使用了 browser 引擎（較慢但品質好）

### Q: 為什麼收到 502 錯誤？
A: 這表示系統負載過高，所有重試都失敗了。請等待 10-30 秒後重試。如果持續發生，可能是高峰時段，建議錯開使用時間。

### Q: Token 是什麼？怎麼計費？
A: Token 是文字處理的計量單位，大約 1 個中文字 = 2-3 個 Token。目前計費方式是每百萬 Token $0.05 美金，可在儀表板和個人資料頁面查看使用量和預估成本。

### Q: 如何減少 Token 使用量？
A: 
1. 使用 target_selector 只提取需要的內容
2. 使用 exclude_selector 排除不需要的元素（如導航、頁尾、廣告）
3. 設置 remove_all_images: true 移除圖片
4. 設置 token_budget 限制最大 Token 數

### Q: API Key 遺失了怎麼辦？
A: 您可以在「API Key 管理」頁面查看所有 API Key，點擊旁邊的複製按鈕即可複製完整的 Key。

### Q: 可以同時發送多個請求嗎？
A: 可以，但建議控制並發數量（建議 2-3 個），並設置請求間隔（至少 3 秒），避免觸發速率限制。
`;
  };

  const handleCopyDoc = async () => {
    try {
      await navigator.clipboard.writeText(generateMarkdownDoc());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const TabButton = ({ tab, label }: { tab: 'guide' | 'api' | 'faq'; label: string }) => (
    <button
      onClick={() => setActiveTab(tab)}
      className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
        activeTab === tab
          ? 'bg-white dark:bg-gray-800 text-primary-600 dark:text-primary-400 border-b-2 border-primary-600'
          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">使用指南</h1>
        <button
          onClick={handleCopyDoc}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          {copied ? (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              已複製
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              複製文檔給 AI
            </>
          )}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        <TabButton tab="guide" label="快速開始" />
        <TabButton tab="api" label="API 技術文檔" />
        <TabButton tab="faq" label="常見問題" />
      </div>

      {/* Guide Tab */}
      {activeTab === 'guide' && (
        <>
          {/* Quick Start */}
          <Card>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">🚀 快速開始</h2>
            </div>
            <div className="p-4 prose dark:prose-invert max-w-none">
              <p>只需三個步驟，即可開始使用爬蟲工具：</p>
              
              <h4>第一步：取得 API Key</h4>
              <ol>
                <li>點擊左側選單的「API Key 管理」</li>
                <li>點擊「建立 API Key」按鈕</li>
                <li>輸入描述（例如：測試用、生產環境）</li>
                <li>複製生成的 API Key（格式：<code>jpp_xxxxxxxx...</code>）</li>
              </ol>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                提示：您可以隨時在 API Key 列表中點擊複製按鈕來複製 Key。
              </p>

              <h4>第二步：測試 API</h4>
              <ol>
                <li>點擊左側選單的「測試區」</li>
                <li>選擇剛才建立的 API Key</li>
                <li>輸入要爬取的網址</li>
                <li>點擊「發送請求」</li>
                <li>查看回應結果</li>
              </ol>

              <h4>第三步：整合到您的專案</h4>
              <p>使用下方「API 技術文檔」分頁的程式碼範例，將 API 整合到您的專案中。</p>
            </div>
          </Card>

          {/* Frontend Features */}
          <Card>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">📱 前端功能說明</h2>
            </div>
            <div className="p-4 space-y-4">
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-semibold text-gray-900 dark:text-white">儀表板</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  查看請求統計（今日、本週、本月）、成功率、Token 使用量和預估成本。可按日期範圍篩選。
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4">
                <h4 className="font-semibold text-gray-900 dark:text-white">API Key 管理</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  建立、啟用/停用、刪除 API Key。每個 Key 都有獨立描述，方便管理多個專案。
                </p>
              </div>
              <div className="border-l-4 border-yellow-500 pl-4">
                <h4 className="font-semibold text-gray-900 dark:text-white">測試區（Playground）</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  選擇 API Key、輸入網址、調整參數，即時測試 API 回應。適合在正式整合前測試參數效果。
                </p>
              </div>
              <div className="border-l-4 border-purple-500 pl-4">
                <h4 className="font-semibold text-gray-900 dark:text-white">請求日誌</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  查看所有 API 請求的歷史紀錄，包含時間、網址、狀態、耗時、Token 使用量。可按日期、狀態篩選。
                </p>
              </div>
              <div className="border-l-4 border-pink-500 pl-4">
                <h4 className="font-semibold text-gray-900 dark:text-white">個人資料</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  查看個人統計和預估成本（每百萬 Token $0.05），修改密碼。
                </p>
              </div>
            </div>
          </Card>
        </>
      )}

      {/* API Tab */}
      {activeTab === 'api' && (
        <>
          {/* Authentication */}
          <Card>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">🔐 認證方式</h2>
            </div>
            <div className="p-4 prose dark:prose-invert max-w-none">
              <p>所有 API 請求都需要在 Header 中包含 API Key：</p>
              <pre className="bg-gray-100 dark:bg-gray-700 p-3 rounded text-sm">Authorization: Bearer YOUR_API_KEY</pre>
              <p className="text-sm text-gray-500">API Key 可在「API Key 管理」頁面取得，格式為 <code>jpp_</code> 開頭的 32 字元字串。</p>
            </div>
          </Card>

          {/* Rate Limiting & Timeout */}
          <Card>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">⏱️ 速率限制與超時設置</h2>
            </div>
            <div className="p-4 prose dark:prose-invert max-w-none">
              <h4>速率限制</h4>
              <ul>
                <li>本服務為多用戶共享平台，實際可用速率約為 <strong>20 RPM</strong></li>
                <li>建議請求間隔至少 <strong>3 秒</strong></li>
                <li>批量處理時使用佇列機制，避免同時發送大量請求</li>
              </ul>

              <h4>回應時間參考</h4>
              <div className="overflow-x-auto not-prose">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-2 text-left">頁面類型</th>
                      <th className="px-4 py-2 text-left">預估時間</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                    <tr><td className="px-4 py-2">簡單靜態頁面</td><td className="px-4 py-2">5-15 秒</td></tr>
                    <tr><td className="px-4 py-2">一般動態頁面</td><td className="px-4 py-2">15-30 秒</td></tr>
                    <tr><td className="px-4 py-2">複雜/長內容頁面</td><td className="px-4 py-2">30-60+ 秒</td></tr>
                  </tbody>
                </table>
              </div>

              <h4 className="mt-4">客戶端超時建議</h4>
              <ul>
                <li>建議設置至少 <strong>120 秒</strong>（2 分鐘）</li>
                <li>使用 <code>browser</code> 引擎或爬取長頁面，建議 <strong>180 秒</strong></li>
                <li className="text-red-600 dark:text-red-400">請勿使用過短的超時時間（如 30 秒），否則長頁面可能會超時失敗</li>
              </ul>
            </div>
          </Card>

          {/* API Endpoint */}
          <Card>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">📡 API 端點</h2>
            </div>
            <div className="p-4 space-y-6">
              <div>
                <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-2">POST /api/v1/read</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">爬取指定網址的內容</p>
                
                {/* Basic Parameters */}
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 mt-4">基本參數</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr><th className="px-4 py-2 text-left">參數</th><th className="px-4 py-2 text-left">類型</th><th className="px-4 py-2 text-left">必填</th><th className="px-4 py-2 text-left">說明</th></tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                      <ParamRow name="url" type="string" required={true} desc="目標網址" />
                      <ParamRow name="engine" type="string" required={false} desc="讀取引擎：browser（品質優先）、direct（速度優先）" />
                      <ParamRow name="format" type="string" required={false} desc="內容格式：markdown（預設）、html、text、screenshot、pageshot" />
                      <ParamRow name="json_response" type="boolean" required={false} desc="固定為 true，不可關閉" />
                      <ParamRow name="timeout" type="number" required={false} desc="超時時間（1-180秒），預設 30 秒" />
                      <ParamRow name="token_budget" type="number" required={false} desc="詞元預算限制" />
                    </tbody>
                  </table>
                </div>

                {/* CSS Selectors */}
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 mt-6">CSS 選擇器</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr><th className="px-4 py-2 text-left">參數</th><th className="px-4 py-2 text-left">類型</th><th className="px-4 py-2 text-left">必填</th><th className="px-4 py-2 text-left">說明</th></tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                      <ParamRow name="target_selector" type="string" required={false} desc="僅提取匹配的元素，例如：article, .main-content" />
                      <ParamRow name="wait_for_selector" type="string" required={false} desc="等待指定元素出現後再提取" />
                      <ParamRow name="exclude_selector" type="string" required={false} desc="排除指定元素，例如：nav, footer, .ads" />
                    </tbody>
                  </table>
                </div>

                {/* Cache & Proxy */}
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 mt-6">快取與代理</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr><th className="px-4 py-2 text-left">參數</th><th className="px-4 py-2 text-left">類型</th><th className="px-4 py-2 text-left">必填</th><th className="px-4 py-2 text-left">說明</th></tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                      <ParamRow name="bypass_cache" type="boolean" required={false} desc="繞過快取，獲取最新內容" />
                      <ParamRow name="no_cache_no_track" type="boolean" required={false} desc="請勿快取或追蹤" />
                      <ParamRow name="use_proxy" type="boolean" required={false} desc="使用代理服務器" />
                      <ParamRow name="proxy_country" type="string" required={false} desc="代理國家代碼（TW、US、JP）" />
                    </tbody>
                  </table>
                </div>

                {/* Content Options */}
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 mt-6">內容選項</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr><th className="px-4 py-2 text-left">參數</th><th className="px-4 py-2 text-left">類型</th><th className="px-4 py-2 text-left">必填</th><th className="px-4 py-2 text-left">說明</th></tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                      <ParamRow name="remove_all_images" type="boolean" required={false} desc="移除所有圖像，減少 Token 使用量" />
                      <ParamRow name="image_caption" type="boolean" required={false} desc="為圖片添加說明標題" />
                      <ParamRow name="stream_mode" type="boolean" required={false} desc="流模式（SSE），適合大型頁面" />
                    </tbody>
                  </table>
                </div>

                <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                  更多進階參數（瀏覽器設定、Markdown 樣式等）請在「測試區」中查看完整選項。
                </p>
              </div>
            </div>
          </Card>

          {/* Code Examples */}
          <Card>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">💻 程式碼範例</h2>
            </div>
            <div className="p-4 space-y-6">
              <div>
                <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-2">cURL</h3>
                <pre className="bg-gray-100 dark:bg-gray-700 p-3 rounded text-sm overflow-x-auto">{`curl -X POST "${baseUrl}/api/v1/read" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"url": "https://example.com"}'`}</pre>
              </div>
              <div>
                <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-2">JavaScript (fetch)</h3>
                <pre className="bg-gray-100 dark:bg-gray-700 p-3 rounded text-sm overflow-x-auto">{`const response = await fetch('${baseUrl}/api/v1/read', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    url: 'https://example.com',
    format: 'markdown'
  })
});
const data = await response.json();
console.log(data);`}</pre>
              </div>
              <div>
                <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-2">Python (requests)</h3>
                <pre className="bg-gray-100 dark:bg-gray-700 p-3 rounded text-sm overflow-x-auto">{`import requests

response = requests.post(
    '${baseUrl}/api/v1/read',
    headers={
        'Authorization': 'Bearer YOUR_API_KEY',
        'Content-Type': 'application/json'
    },
    json={
        'url': 'https://example.com',
        'format': 'markdown'
    },
    timeout=120  # 建議至少 120 秒
)
data = response.json()
print(data)`}</pre>
              </div>
            </div>
          </Card>

          {/* Response Format */}
          <Card>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">📄 回應格式</h2>
            </div>
            <div className="p-4 space-y-6">
              <div>
                <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-2">成功回應</h3>
                <pre className="bg-gray-100 dark:bg-gray-700 p-3 rounded text-sm overflow-x-auto">{`{
  "code": 200,
  "status": 20000,
  "data": {
    "title": "Example Domain",
    "url": "https://example.com/",
    "content": "# Example Domain\\n\\n...",
    "usage": { "tokens": 150 }
  }
}`}</pre>
              </div>
              <div>
                <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-2">錯誤回應</h3>
                <pre className="bg-gray-100 dark:bg-gray-700 p-3 rounded text-sm overflow-x-auto">{`{
  "success": false,
  "error": {
    "code": "VALIDATION_ERROR",
    "message": "缺少必要參數: url"
  }
}`}</pre>
              </div>
            </div>
          </Card>

          {/* Error Codes */}
          <Card>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">⚠️ 錯誤代碼</h2>
            </div>
            <div className="p-4">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-2 text-left">HTTP</th>
                      <th className="px-4 py-2 text-left">錯誤代碼</th>
                      <th className="px-4 py-2 text-left">說明</th>
                      <th className="px-4 py-2 text-left">處理方式</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                    <tr>
                      <td className="px-4 py-2">400</td>
                      <td className="px-4 py-2"><code>VALIDATION_ERROR</code></td>
                      <td className="px-4 py-2">請求參數錯誤</td>
                      <td className="px-4 py-2">檢查參數格式，確保 url 存在</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2">401</td>
                      <td className="px-4 py-2"><code>INVALID_TOKEN</code></td>
                      <td className="px-4 py-2">API Key 無效</td>
                      <td className="px-4 py-2">檢查 API Key 是否正確、已啟用</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2">502</td>
                      <td className="px-4 py-2"><code>ALL_RETRIES_FAILED</code></td>
                      <td className="px-4 py-2">系統負載過高</td>
                      <td className="px-4 py-2">等待 10-30 秒後重試</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        </>
      )}

      {/* FAQ Tab */}
      {activeTab === 'faq' && (
        <>
          <Card>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">❓ 常見問題</h2>
            </div>
            <div className="p-4 space-y-6">
              <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Q: 為什麼請求一直超時？</h4>
                <div className="text-gray-600 dark:text-gray-400 text-sm space-y-1">
                  <p>可能原因：</p>
                  <ul className="list-disc list-inside ml-2">
                    <li>目標網站回應慢或內容很長</li>
                    <li>客戶端超時設置太短（建議至少 120 秒）</li>
                    <li>使用了 browser 引擎（較慢但品質好）</li>
                  </ul>
                  <p className="mt-2">解決方式：增加客戶端超時時間，或改用 direct 引擎。</p>
                </div>
              </div>

              <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Q: 為什麼收到 502 錯誤（ALL_RETRIES_FAILED）？</h4>
                <div className="text-gray-600 dark:text-gray-400 text-sm">
                  <p>這表示系統負載過高，所有重試都失敗了。</p>
                  <p className="mt-2">解決方式：等待 10-30 秒後重試。如果持續發生，可能是高峰時段，建議錯開使用時間或降低請求頻率。</p>
                </div>
              </div>

              <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Q: Token 是什麼？怎麼計費？</h4>
                <div className="text-gray-600 dark:text-gray-400 text-sm">
                  <p>Token 是文字處理的計量單位，大約 1 個中文字 = 2-3 個 Token。</p>
                  <p className="mt-2">計費方式：每百萬 Token <strong>$0.05 美金</strong></p>
                  <p className="mt-2">可在「儀表板」和「個人資料」頁面查看使用量和預估成本。</p>
                </div>
              </div>

              <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Q: 如何減少 Token 使用量？</h4>
                <div className="text-gray-600 dark:text-gray-400 text-sm">
                  <ul className="list-disc list-inside space-y-1">
                    <li>使用 <code>target_selector</code> 只提取需要的內容</li>
                    <li>使用 <code>exclude_selector</code> 排除不需要的元素（導航、頁尾、廣告）</li>
                    <li>設置 <code>remove_all_images: true</code> 移除圖片</li>
                    <li>設置 <code>token_budget</code> 限制最大 Token 數</li>
                  </ul>
                </div>
              </div>

              <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Q: API Key 遺失了怎麼辦？</h4>
                <div className="text-gray-600 dark:text-gray-400 text-sm">
                  <p>API Key 只會在建立時顯示一次，無法找回。</p>
                  <p className="mt-2">解決方式：到「API Key 管理」頁面刪除舊的 Key，重新建立一個新的。</p>
                </div>
              </div>

              <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Q: 可以同時發送多個請求嗎？</h4>
                <div className="text-gray-600 dark:text-gray-400 text-sm">
                  <p>可以，但建議：</p>
                  <ul className="list-disc list-inside ml-2">
                    <li>控制並發數量（建議 2-3 個）</li>
                    <li>設置請求間隔（至少 3 秒）</li>
                    <li>使用佇列機制處理批量請求</li>
                  </ul>
                </div>
              </div>

              <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Q: 為什麼爬取結果是空的或不完整？</h4>
                <div className="text-gray-600 dark:text-gray-400 text-sm">
                  <p>可能原因：</p>
                  <ul className="list-disc list-inside ml-2">
                    <li>目標網站需要 JavaScript 渲染 → 改用 <code>browser</code> 引擎</li>
                    <li>內容在 iframe 中 → 設置 <code>include_iframe: true</code></li>
                    <li>需要等待動態載入 → 使用 <code>wait_for_selector</code></li>
                    <li>目標網站有反爬蟲機制 → 嘗試使用代理</li>
                  </ul>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Q: 如何聯繫技術支援？</h4>
                <div className="text-gray-600 dark:text-gray-400 text-sm">
                  <p>請聯繫系統管理員或透過內部溝通管道反映問題。</p>
                </div>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
